import type { GroupSummary, TenuredGroup } from "@/lib/api/messages";

/**
 * "You and this person are both members of X."
 *
 * Deliberately NOT AffiliationChip: that chip's icon and title assert
 * "Administrator i {name}", which would be a lie here — a shared group says
 * nothing about either party's role. It is also a <Link>, and this chip renders
 * inside cards that host buttons, where a nested anchor is invalid markup.
 */
export function SharedGroupChip({ group }: { group: GroupSummary }) {
  return (
    <span
      title={`I er begge medlemmer af ${group.name}`}
      className="inline-flex items-center gap-1 rounded-full bg-primary-muted/50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-primary-muted-foreground"
    >
      <svg
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
      <span className="truncate max-w-[10rem]">{group.name}</span>
    </span>
  );
}

/**
 * The shared-group row of a request card, with its own empty state.
 *
 * `sharedGroups` carries each group's MEMBER TENURE now (§2.14 folded
 * `/details` in), which the details pane reads; a chip only needs the group.
 */
export function SharedGroupChips({ groups }: { groups: TenuredGroup[] }) {
  if (groups.length === 0) {
    return (
      <p className="text-xs text-subtle-foreground">
        Ingen fælles grupper
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {groups.map(({ group }) => (
        <SharedGroupChip key={group.id} group={group} />
      ))}
    </div>
  );
}
